import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANG_COLORS, getLangName } from '../../data';
import { useBuilderContext } from '../../context/BuilderContext';
import { useGdprOuKey } from '@/hooks/useOUs';

export function TranslationsSection() {
  const { t } = useTranslation();
  const { form, setForm } = useBuilderContext();

  const gdprOuKey = useGdprOuKey();
  const isEuropa = !!gdprOuKey && form.audienceOUs.includes(gdprOuKey);
  const transLangs = isEuropa
    ? ['en', 'pt', 'fr', 'de', 'nl', 'ro']
    : ['en'];
  const [activeLang, setActiveLang] = useState(transLangs[0]);

  const getRequiredTrlKeys = (): string[] => {
    const keys: string[] = ['survey_name'];
    form.questions.forEach((q, qi) => {
      keys.push(`q_${qi}`);
      const hasOpts = q.type === 'multiple' || (q.type === 'behaviors' && q.enfoque === 'individual');
      if (hasOpts) q.options.forEach((_, oi) => keys.push(`q_${qi}_opt_${oi}`));
    });
    return keys;
  };

  const getLangCompleteness = (lang: string) => {
    const enabled = form.translationsEnabled[lang] !== false;
    const required = getRequiredTrlKeys();
    const total = required.length;
    const filled = required.filter(k => form.translations?.[lang]?.[k]?.trim()).length;
    return { enabled, filled, total, complete: enabled && total > 0 && filled === total };
  };

  const updateTrl = (lang: string, key: string, val: string) => {
    setForm(p => ({
      ...p,
      translations: {
        ...p.translations,
        [lang]: { ...(p.translations[lang] || {}), [key]: val },
      },
    }));
  };

  const autoTranslate = (lang: string) => {
    const prefix = `[${lang.toUpperCase()}]`;
    setForm(p => {
      const existing = p.translations[lang] || {};
      const upd: Record<string, string> = { ...existing };
      if (!upd['survey_name']?.trim()) upd['survey_name'] = `${prefix} ${p.name}`;
      if (p.description && !upd['survey_desc']?.trim()) upd['survey_desc'] = `${prefix} ${p.description}`;
      p.questions.forEach((q, qi) => {
        if (!upd[`q_${qi}`]?.trim()) upd[`q_${qi}`] = `${prefix} ${q.text || ''}`;
        const hasOpts = q.type === 'multiple' || (q.type === 'behaviors' && q.enfoque === 'individual');
        if (hasOpts) q.options.forEach((opt, oi) => {
          if (!upd[`q_${qi}_opt_${oi}`]?.trim()) upd[`q_${qi}_opt_${oi}`] = `${prefix} ${opt}`;
        });
      });
      return { ...p, translations: { ...p.translations, [lang]: upd } };
    });
  };

  return (
    <div className="builder-section">
      <div className="cfg-section-head">
        <div>
          <h3><i className="fas fa-language" /> {t('admin.builderTranslationsTitle')}</h3>
          <p className="cfg-section-sub">
            {isEuropa
              ? t('admin.builderTranslationsEuropeSub')
              : t('admin.builderTranslationsSub')}
          </p>
        </div>
      </div>

      <div className="info-banner-small" style={{ marginBottom: 14 }}>
        <i className="fas fa-robot" style={{ color: '#342F82' }} />
        <p>{t('admin.builderAutoTranslateNote')}</p>
      </div>

      {/* Tabs de idiomas */}
      <div className="lang-tabs-bar">
        {transLangs.map(lang => {
          const { enabled, filled, total, complete } = getLangCompleteness(lang);
          const partial = enabled && filled > 0 && filled < total;
          return (
            <button
              key={lang}
              className={`lang-tab-btn${activeLang === lang ? ' active' : ''}`}
              onClick={() => setActiveLang(lang)}
            >
              <i className="fas fa-globe" /> {getLangName(lang, t)}
              {!enabled
                ? <span className="lang-tab-badge disabled">{t('admin.builderLangNotApplicable')}</span>
                : complete
                ? <span className="lang-tab-badge complete"><i className="fas fa-check" /> {t('admin.builderLangComplete')}</span>
                : partial
                ? <span className="lang-tab-badge partial">{filled}/{total}</span>
                : <span className="lang-tab-badge empty">{t('admin.builderLangPending')}</span>
              }
            </button>
          );
        })}
      </div>

      {/* Panel activo */}
      {transLangs.map(lang => {
        if (lang !== activeLang) return null;
        const enabled = form.translationsEnabled[lang] !== false;
        const langColor = LANG_COLORS[lang] || '#342F82';
        return (
          <div key={lang} className="lang-panel">
            <div className="lang-panel-header">
              <div className="lang-panel-title">
                <i className="fas fa-language" /> {getLangName(lang, t)}
                {isEuropa && (
                  <span className="cfg-admin-tag" style={{ marginLeft: 6 }}>
                    {t('admin.builderLangRequired')}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {enabled && (
                  <button className="trl-auto-btn" onClick={() => autoTranslate(lang)}>
                    <i className="fas fa-robot" /> {t('admin.builderAutoTranslateBtn')}
                  </button>
                )}
                <label className="lang-toggle-label">
                  <span className={enabled ? 'lang-toggle-applies' : 'lang-toggle-noapply'}>
                    {enabled
                      ? <><i className="fas fa-check-circle" /> {t('admin.builderLangApplies')}</>
                      : <><i className="fas fa-times-circle" /> {t('admin.builderLangDoesNotApply')}</>}
                  </span>
                  <div
                    className={`lang-toggle-switch${enabled ? ' on' : ''}`}
                    onClick={() => setForm(p => ({
                      ...p,
                      translationsEnabled: { ...p.translationsEnabled, [lang]: !enabled },
                    }))}
                  >
                    <div className="lang-toggle-knob" />
                  </div>
                </label>
              </div>
            </div>

            {enabled ? (
              <div className="trl-questions-list">
                {/* Nombre de encuesta */}
                {(() => {
                  const trlKey = 'survey_name';
                  const val = form.translations?.[lang]?.[trlKey] || '';
                  const done = val.trim().length > 0;
                  return (
                    <div className={`trl-row${done ? ' trl-row--done' : ''}`}>
                      <div className="trl-row-num">
                        <i className="fas fa-tag" style={{ fontSize: '0.65rem', opacity: 0.6 }} />
                      </div>
                      <div className="trl-row-body">
                        <div className="trl-original">
                          <span className="trl-lang-pill es">ES</span>
                          <span className="trl-original-text trl-field-label">
                            {t('admin.builderTrlName')}{' '}
                            <em>{form.name || <span style={{ color: '#ccc' }}>{t('admin.builderTrlEmpty')}</span>}</em>
                          </span>
                        </div>
                        <div className="trl-input-wrap">
                          <span className="trl-lang-pill" style={{ background: langColor }}>
                            {lang.toUpperCase()}
                          </span>
                          <input
                            type="text"
                            className="trl-input"
                            value={val}
                            placeholder={`${t('admin.builderTrlPlaceholderIn')} ${getLangName(lang, t)}…`}
                            onChange={e => updateTrl(lang, trlKey, e.target.value)}
                          />
                          {done && <i className="fas fa-check-circle trl-check" />}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Descripción (si existe) */}
                {form.description && (() => {
                  const trlKey = 'survey_desc';
                  const val = form.translations?.[lang]?.[trlKey] || '';
                  const done = val.trim().length > 0;
                  return (
                    <div className={`trl-row${done ? ' trl-row--done' : ''}`}>
                      <div className="trl-row-num">
                        <i className="fas fa-align-left" style={{ fontSize: '0.65rem', opacity: 0.6 }} />
                      </div>
                      <div className="trl-row-body">
                        <div className="trl-original">
                          <span className="trl-lang-pill es">ES</span>
                          <span className="trl-original-text trl-field-label">
                            {t('admin.builderTrlDesc')} <em>{form.description}</em>
                          </span>
                        </div>
                        <div className="trl-input-wrap">
                          <span className="trl-lang-pill" style={{ background: langColor }}>
                            {lang.toUpperCase()}
                          </span>
                          <input
                            type="text"
                            className="trl-input"
                            value={val}
                            placeholder={`${t('admin.builderTrlPlaceholderIn')} ${getLangName(lang, t)}…`}
                            onChange={e => updateTrl(lang, trlKey, e.target.value)}
                          />
                          {done && <i className="fas fa-check-circle trl-check" />}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Preguntas y opciones */}
                {form.questions.map((q, qi) => {
                  const qKey = `q_${qi}`;
                  const qVal = form.translations?.[lang]?.[qKey] || '';
                  const qDone = qVal.trim().length > 0;
                  const hasOpts = q.type === 'multiple' || (q.type === 'behaviors' && q.enfoque === 'individual');
                  return (
                    <div key={q.id} style={{ display: 'contents' }}>
                      <div className={`trl-row${qDone ? ' trl-row--done' : ''}`}>
                        <div className="trl-row-num">{qi + 1}</div>
                        <div className="trl-row-body">
                          <div className="trl-original">
                            <span className="trl-lang-pill es">ES</span>
                            <span className="trl-original-text">
                              {q.text || <em style={{ color: '#ccc' }}>{t('admin.builderTrlNoText')}</em>}
                            </span>
                          </div>
                          <div className="trl-input-wrap">
                            <span className="trl-lang-pill" style={{ background: langColor }}>
                              {lang.toUpperCase()}
                            </span>
                            <input
                              type="text"
                              className="trl-input"
                              value={qVal}
                              placeholder={`${t('admin.builderTrlPlaceholderIn')} ${getLangName(lang, t)}…`}
                              onChange={e => updateTrl(lang, qKey, e.target.value)}
                            />
                            {qDone && <i className="fas fa-check-circle trl-check" />}
                          </div>
                        </div>
                      </div>

                      {hasOpts && q.options.length > 0 && (
                        <div className="trl-opts-list">
                          {q.options.map((opt, oi) => {
                            const optKey = `q_${qi}_opt_${oi}`;
                            const optVal = form.translations?.[lang]?.[optKey] || '';
                            const optDone = optVal.trim().length > 0;
                            return (
                              <div key={oi} className={`trl-row trl-row--opt${optDone ? ' trl-row--done' : ''}`}>
                                <div className="trl-row-num">
                                  <i className="fas fa-list-ul" style={{ fontSize: '0.65rem', opacity: 0.5 }} />
                                </div>
                                <div className="trl-row-body">
                                  <div className="trl-original">
                                    <span className="trl-lang-pill es">ES</span>
                                    <span className="trl-original-text" style={{ fontSize: '0.74rem' }}>
                                      {opt || <em style={{ color: '#ccc' }}>{t('admin.builderTrlEmpty')}</em>}
                                    </span>
                                  </div>
                                  <div className="trl-input-wrap">
                                    <span className="trl-lang-pill" style={{ background: langColor }}>
                                      {lang.toUpperCase()}
                                    </span>
                                    <input
                                      type="text"
                                      className="trl-input"
                                      value={optVal}
                                      placeholder={`${t('admin.builderTrlPlaceholderOpt')} ${oi + 1} ${t('admin.builderTrlPlaceholderIn')} ${getLangName(lang, t)}…`}
                                      onChange={e => updateTrl(lang, optKey, e.target.value)}
                                    />
                                    {optDone && <i className="fas fa-check-circle trl-check" />}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="trl-noapply-msg">
                <i className="fas fa-info-circle" /> {t('admin.builderLangDisabled')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
